import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Upload,
  MapPin,
  CheckCircle2,
  XCircle,
  Building2,
  Loader2,
} from 'lucide-react';
import { loadModules } from 'esri-loader';
import Header from '../../layout/Header';
import { useAuth } from '../../../AuthProvider';
import {
  parseCSV,
  extractUniqueBuildings,
  processBuildings,
} from '../../../utils/boundaryChecker';

const BoundaryChecker = () => {
  const { getAccessToken } = useAuth();
  const [mapView, setMapView] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, address: '' });
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const graphicsLayerRef = useRef(null);
  const boundaryLayerRef = useRef(null);

  useEffect(() => {
    let mapViewInstance;
    let graphicsLayer;
    let boundaryLayer;

    loadModules([
      'esri/Map',
      'esri/views/MapView',
      'esri/layers/GraphicsLayer',
      'esri/layers/FeatureLayer',
      'esri/Graphic',
      'esri/geometry/Point',
      'esri/symbols/SimpleMarkerSymbol',
      'esri/symbols/SimpleFillSymbol',
      'esri/symbols/SimpleLineSymbol',
      'esri/geometry/Polygon',
    ]).then(([
      Map,
      MapView,
      GraphicsLayer,
      FeatureLayer,
      Graphic,
      Point,
      SimpleMarkerSymbol,
      SimpleFillSymbol,
      SimpleLineSymbol,
      Polygon,
    ]) => {
      const map = new Map({
        basemap: 'streets-vector',
      });

      mapViewInstance = new MapView({
        container: mapRef.current,
        map: map,
        center: [-94.583331, 39.099727],
        zoom: 11,
      });

      graphicsLayer = new GraphicsLayer();
      map.add(graphicsLayer);
      graphicsLayerRef.current = graphicsLayer;

      const kansasCityMetroLayer = new FeatureLayer({
        url: 'https://services5.arcgis.com/vElyTHUSDMtSHSgT/arcgis/rest/services/Kansas_City_counties/FeatureServer/0',
        title: 'Kansas City Metropolitan Area',
        opacity: 0.5,
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-fill',
            color: [0, 100, 200, 0.2],
            outline: {
              type: 'simple-line',
              color: [0, 100, 200, 0.8],
              width: 2,
            },
          },
        },
        popupEnabled: false,
      });

      map.add(kansasCityMetroLayer);
      boundaryLayerRef.current = kansasCityMetroLayer;

      setMapView(mapViewInstance);
    }).catch((error) => {
      console.error('Error loading ArcGIS modules:', error);
      setError('Failed to initialize map');
    });

    return () => {
      if (mapViewInstance) {
        mapViewInstance.destroy();
      }
    };
  }, []);


  useEffect(() => {
    if (!mapView || !graphicsLayerRef.current || buildings.length === 0) return;

    graphicsLayerRef.current.removeAll();

    buildings.forEach((building) => {
      if (!building.coordinates || !building.longitude || !building.latitude) return;

      loadModules([
        'esri/Graphic',
        'esri/geometry/Point',
        'esri/symbols/SimpleMarkerSymbol',
      ]).then(([Graphic, Point, SimpleMarkerSymbol]) => {
        const point = new Point({
          longitude: building.longitude,
          latitude: building.latitude,
          spatialReference: { wkid: 4326 },
        });

        const markerSymbol = new SimpleMarkerSymbol({
          style: 'circle',
          color: building.isWithinKansasCity ? [34, 197, 94, 0.8] : [239, 68, 68, 0.8],
          size: 12,
          outline: {
            color: building.isWithinKansasCity ? [22, 163, 74, 1] : [220, 38, 38, 1],
            width: 2,
          },
        });

        const graphic = new Graphic({
          geometry: point,
          symbol: markerSymbol,
          attributes: {
            address: building.buildingAddress,
            name: building.buildingName,
            units: building.totalUnits,
            isWithin: building.isWithinKansasCity,
          },
        });

        graphicsLayerRef.current.add(graphic);
      });
    });

    if (buildings.length > 0 && buildings.some(b => b.coordinates)) {
      const validBuildings = buildings.filter(b => b.coordinates);
      const longitudes = validBuildings.map(b => b.longitude);
      const latitudes = validBuildings.map(b => b.latitude);
      
      const centerLon = (Math.min(...longitudes) + Math.max(...longitudes)) / 2;
      const centerLat = (Math.min(...latitudes) + Math.max(...latitudes)) / 2;
      
      mapView.goTo({
        center: [centerLon, centerLat],
        zoom: 12,
      });
    }
  }, [buildings, mapView]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setBuildings([]);

    try {
      const csvData = await parseCSV(file);
      const uniqueBuildings = extractUniqueBuildings(csvData);
      
      if (uniqueBuildings.length === 0) {
        setError('No valid buildings found in CSV file');
        setLoading(false);
        return;
      }

      setProcessing(true);
      const accessToken = getAccessToken();
      
      const processedBuildings = await processBuildings(
        uniqueBuildings,
        accessToken,
        (current, total, address) => {
          setProgress({ current, total, address });
        }
      );

      setBuildings(processedBuildings);
      setProcessing(false);
    } catch (err) {
      console.error('Error processing CSV:', err);
      setError(err.message || 'Failed to process CSV file');
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    total: buildings.length,
    inside: buildings.filter(b => b.isWithinKansasCity).length,
    outside: buildings.filter(b => !b.isWithinKansasCity && b.geocoded).length,
    failed: buildings.filter(b => !b.geocoded).length,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Header />
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, position: 'relative' }}>
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
          
          {processing && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: 'white',
                padding: 2,
                borderRadius: 2,
                boxShadow: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} />
                <Box>
                  <Typography variant="body2">
                    Processing {progress.current} of {progress.total} buildings
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {progress.address}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            width: 400,
            backgroundColor: '#F9FAFB',
            borderLeft: '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #E5E7EB' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Kansas City Boundary Checker
            </Typography>
            
            <Button
              variant="contained"
              component="label"
              startIcon={<Upload size={18} />}
              fullWidth
              disabled={loading || processing}
              sx={{ mb: 2 }}
            >
              Upload CSV
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileUpload}
              />
            </Button>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {stats.total > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Card sx={{ flex: 1, minWidth: 100 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Total
                    </Typography>
                    <Typography variant="h6">{stats.total}</Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1, minWidth: 100 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Inside KC
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#22c55e' }}>
                      {stats.inside}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1, minWidth: 100 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Outside KC
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#ef4444' }}>
                      {stats.outside}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {buildings.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                <Building2 size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                <Typography variant="body2">
                  Upload a CSV file to check building locations
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Address</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {buildings.map((building, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {building.buildingName || 'Unnamed'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {building.buildingAddress}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {building.geocoded ? (
                            <Chip
                              icon={building.isWithinKansasCity ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                              label={building.isWithinKansasCity ? 'Inside' : 'Outside'}
                              size="small"
                              color={building.isWithinKansasCity ? 'success' : 'error'}
                            />
                          ) : (
                            <Chip
                              icon={<Loader2 size={14} />}
                              label="Failed"
                              size="small"
                              color="default"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BoundaryChecker;
